import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button } from './ui/button'
import { FaBars, FaTimes, FaWallet, FaGlobe } from 'react-icons/fa'
import { motion, AnimatePresence } from 'framer-motion'
import { useWallet } from '../contexts/WalletContext'
import { WalletModal } from './WalletModal'
import { TierBadge } from './TierBadge'

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [walletModalOpen, setWalletModalOpen] = useState(false)
  const { isConnected, formatAddress, account } = useWallet()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // The Trinity of Trust - Core Navigation from Design Doc
  const navItems = [
    { name: 'Dashboard', href: '/dashboard', isRoute: true },
    { name: 'Team', href: '/team', isRoute: true },
    { name: 'Whitepaper', href: '/whitepaper', isRoute: true },
    { name: 'Presale', href: '#presale', highlight: true },
    { name: 'Community', href: '#community' }
  ]

  return (
    <motion.nav 
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-deep-charcoal/95 backdrop-blur-xl border-b border-electric-blue/20 shadow-deep-tech' 
          : 'bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Following Design Branding */}
          <motion.div 
            className="flex items-center space-x-4"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            <div className="w-12 h-12 bg-electric-gradient rounded-xl flex items-center justify-center shadow-electric">
              <span className="text-deep-charcoal font-heading font-black text-xl">B</span>
            </div>
            <div>
              <div className="font-heading text-2xl font-black text-bright-white tracking-tight">
                BUYMEABODY
              </div>
              <div className="font-body text-xs text-electric-blue font-bold tracking-widest uppercase">
                .ROBOTICS
              </div>
            </div>
          </motion.div>
          
          {/* Desktop Navigation - The Trinity of Trust */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              item.isRoute ? (
                <Link
                  key={item.name}
                  to={item.href}
                  className="font-heading px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wider transition-all duration-200 text-cyan-400 bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 hover:border-cyan-500/50"
                >
                  {item.name}
                </Link>
              ) : (
                <a
                  key={item.name}
                  href={item.href}
                  className={`font-heading px-4 py-2 rounded-lg text-sm font-bold uppercase tracking-wider transition-all duration-200 ${
                    item.highlight
                      ? 'text-yellow-400 bg-yellow-500/10 border border-yellow-500/30 hover:bg-yellow-500/20 hover:border-yellow-500/50 animate-pulse-subtle'
                      : 'text-subtle-gray hover:text-electric-blue hover:bg-electric-blue/10 border border-transparent hover:border-electric-blue/20'
                  }`}
                >
                  {item.name}
                  {item.highlight && <span className="ml-2">🚀</span>}
                </a>
              )
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Tier Badge (if connected) */}
            {isConnected && <TierBadge />}
            
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-subtle-gray hover:text-electric-blue"
            >
              <FaGlobe className="mr-2" />
              EN
            </Button>
            <Button 
              size="sm" 
              variant="primary" 
              className="shadow-electric"
              onClick={() => setWalletModalOpen(true)}
            >
              <FaWallet className="mr-2" />
              {isConnected ? formatAddress(account) : 'Connect Wallet'}
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-subtle-gray hover:text-electric-blue p-2 rounded-lg border border-electric-blue/30 hover:border-electric-blue/60 hover:bg-electric-blue/10 transition-all duration-200"
            >
              {isOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-deep-charcoal/98 backdrop-blur-xl border-b border-electric-blue/20"
          >
            <div className="px-4 pt-2 pb-6 space-y-2">
              {navItems.map((item) => (
                item.isRoute ? (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="block font-heading text-cyan-400 px-4 py-3 rounded-lg text-base font-bold uppercase tracking-wider bg-cyan-500/10 border border-cyan-500/30 hover:bg-cyan-500/20 transition-all duration-200"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                ) : (
                  <a
                    key={item.name}
                    href={item.href}
                    className={`block font-heading px-4 py-3 rounded-lg text-base font-bold uppercase tracking-wider border border-transparent transition-all duration-200 ${
                      item.highlight
                        ? 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30'
                        : 'text-subtle-gray hover:text-electric-blue hover:bg-electric-blue/10 hover:border-electric-blue/20'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                    {item.highlight && <span className="ml-2">🚀</span>}
                  </a>
                )
              ))}
              <div className="pt-4 space-y-3 border-t border-electric-blue/20">
                <Button variant="ghost" size="sm" className="w-full justify-center">
                  <FaGlobe className="mr-2" />
                  Language
                </Button>
                <Button 
                  size="sm" 
                  variant="primary" 
                  className="w-full shadow-electric"
                  onClick={() => {
                    setWalletModalOpen(true)
                    setIsOpen(false)
                  }}
                >
                  <FaWallet className="mr-2" />
                  {isConnected ? formatAddress(account) : 'Connect Wallet'}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Wallet Modal */}
      <WalletModal 
        isOpen={walletModalOpen} 
        onClose={() => setWalletModalOpen(false)} 
      />
    </motion.nav>
  )
}